import { useEffect } from 'react';
import { getUserBudget } from '@/lib/api/user-budget.client';
import { useOnTabReveal } from '@/lib/hooks/use-on-tab-reveal';
import { create } from 'zustand';
import { useSessionStore } from './session.store';

export type BudgetStatus = 'ok' | 'warning' | 'exceeded';

/**
 * Details for the budget/overshoot limit alert shown inline in the thread.
 * Mirrors the backend's `error.details` payload for 429 responses with
 * code `budget_exhausted` or `user_overshoot_limit`.
 */
export interface BudgetLimitDetails {
  blockedReason: string;
  resetDate: string;
  upgradeUrl?: string;
  buyCreditsUrl?: string;
}

/**
 * Ephemeral alert state shown when the backend returns a 429 limit error.
 * Story 22.1: replaced the old magic-prefix-based approach (where the alert
 * was injected as a fake assistant message) with a store-driven render.
 * The alert is rendered directly below the last thread message and cleared
 * on the next successful send.
 */
export type LimitAlert = { type: 'trial' } | { type: 'budget'; details: BudgetLimitDetails };

export type WarningReason = 'personal' | 'workspace';

export interface BudgetState {
  usagePercent: number | null;
  /** Spend against the user's own quota; may exceed 100. See the client type. */
  personalUsagePercent: number | null;
  status: BudgetStatus | null;
  projectedLimitDate: string | null;
  resetDate: string | null;
  usingCredits: boolean;
  // Story 17.18 — workspace-level visibility
  workspaceUsagePercent: number | null;
  warningReason: WarningReason | null;
  agentSpendPercent: number;
  // Remaining purchased credit (USD) — for the admin "läuft auf Guthaben (noch X übrig)" line.
  creditRemaining: number;
  // Sparmodus flag: true when the paid user has exhausted their personal cap
  // or the tenant pool. The model selector hides non-low_budget_allowed
  // models, and the default model auto-switches to one that stays open.
  lowBudgetMode: boolean;
  isLoading: boolean;
  hasLoaded: boolean;
  /** Epoch ms of the last successful (or failed) fetch — drives `maxAgeMs`. */
  lastLoadedAt: number | null;
  limitAlert: LimitAlert | null;
  /**
   * `force` re-fetches regardless of `hasLoaded`. `maxAgeMs` re-fetches only
   * when the cached state is older than that — for cheap opportunistic
   * refreshes (model selector opened, tab regained focus) that must not turn
   * into a request storm.
   */
  loadBudget: (options?: { force?: boolean; maxAgeMs?: number }) => Promise<void>;
  setLimitAlert: (alert: LimitAlert) => void;
  clearLimitAlert: () => void;
  reset: () => void;
}

export const useBudgetStore = create<BudgetState>((set, get) => ({
  usagePercent: null,
  personalUsagePercent: null,
  status: null,
  projectedLimitDate: null,
  resetDate: null,
  usingCredits: false,
  workspaceUsagePercent: null,
  warningReason: null,
  agentSpendPercent: 0,
  creditRemaining: 0,
  lowBudgetMode: false,
  isLoading: false,
  hasLoaded: false,
  lastLoadedAt: null,
  limitAlert: null,

  loadBudget: async (options) => {
    const force = options?.force ?? false;
    const maxAgeMs = options?.maxAgeMs;
    if (get().isLoading) return;
    if (maxAgeMs !== undefined) {
      const last = get().lastLoadedAt;
      if (last !== null && Date.now() - last < maxAgeMs) return;
    } else if (get().hasLoaded && !force) {
      return;
    }

    set({ isLoading: true });
    try {
      const data = await getUserBudget();
      set({
        usagePercent: data.usagePercent,
        personalUsagePercent: data.personalUsagePercent ?? null,
        status: data.status,
        projectedLimitDate: data.projectedLimitDate,
        resetDate: data.resetDate,
        usingCredits: data.usingCredits ?? false,
        workspaceUsagePercent: data.workspaceUsagePercent ?? 0,
        warningReason: data.warningReason ?? null,
        agentSpendPercent: data.agentSpendPercent ?? 0,
        creditRemaining: data.creditRemaining ?? 0,
        lowBudgetMode: data.lowBudgetMode ?? false,
        isLoading: false,
        hasLoaded: true,
        lastLoadedAt: Date.now(),
      });
    } catch {
      set({
        isLoading: false,
        hasLoaded: true,
        lastLoadedAt: Date.now(),
      });
    }
  },

  setLimitAlert: (alert) => set({ limitAlert: alert }),
  clearLimitAlert: () => set({ limitAlert: null }),

  reset: () => {
    set({
      usagePercent: null,
      personalUsagePercent: null,
      status: null,
      projectedLimitDate: null,
      resetDate: null,
      usingCredits: false,
      workspaceUsagePercent: null,
      warningReason: null,
      agentSpendPercent: 0,
      creditRemaining: 0,
      lowBudgetMode: false,
      isLoading: false,
      hasLoaded: false,
      lastLoadedAt: null,
      limitAlert: null,
    });
  },
}));

/**
 * Keep the budget current while the app is open: once the session is known,
 * then every two minutes, and whenever the tab comes back to the front. The
 * sidebar shows the result, and the chat reads the low-budget flag from it to
 * move the model selection onto a model that stays open.
 *
 * Mounted once, in the sidebar shell (``AppSidebarShell``), which every app
 * section keeps mounted on every screen size. It used to live in the sidebar's
 * budget widget, which a phone only renders while its drawer is open: there the
 * budget stayed unknown and the low-budget model swap waited until the model
 * selector was opened (KAN-1748). One place, the same requests as before.
 */
export function useBudgetRefresh(): void {
  const sessionHasLoaded = useSessionStore((state) => state.hasLoaded);
  const userId = useSessionStore((state) => state.profile?.sub);
  const loadBudget = useBudgetStore((state) => state.loadBudget);

  useEffect(() => {
    if (!sessionHasLoaded || !userId) return;
    loadBudget();
    const interval = setInterval(() => loadBudget({ force: true }), 120_000);
    return () => clearInterval(interval);
  }, [sessionHasLoaded, userId, loadBudget]);

  // Browsers throttle timers in background tabs (Chrome down to once a
  // minute, less under intensive throttling), so the interval alone can leave
  // a stale low-budget flag on screen for several minutes after a licence
  // upgrade. Refresh whenever the tab comes back to the front; `maxAgeMs`
  // keeps quick back-and-forth from asking again.
  useOnTabReveal(() => {
    if (sessionHasLoaded && userId) loadBudget({ maxAgeMs: 30_000 });
  }, 1_000);
}
